// Gerencia document.title com contadores ao vivo. Sem render visual (retorna null).
// Montado no AppShell (layout autenticado).
//
// Regras:
//   - só mensagens não lidas  → "(N mensagens) ChatIA" (singular "(1 mensagem)")
//   - só conversas na fila     → "(N na fila) ChatIA"
//   - os dois > 0              → alterna entre os formatos a cada 4s
//   - ambos zerados / unmount  → volta a "ChatIA"
// N mensagens = SOMA das não-lidas por conversa; N fila = filtro `aguardando`.
import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/app/store/hooks';
import { selectUnreadByConversation } from '@/features/notifications';
import { useQueueQuery } from '@/features/queue';

const DEFAULT_TITLE = 'ChatIA';
const ALTERNATE_MS = 4000;

function messagesTitle(n: number): string {
  return n === 1 ? '(1 mensagem) ChatIA' : `(${n} mensagens) ChatIA`;
}

function queueTitle(n: number): string {
  return `(${n} na fila) ChatIA`;
}

export function TabTitleManager() {
  const unreadByConversation = useAppSelector(selectUnreadByConversation);
  const messages = useMemo(
    () => Object.values(unreadByConversation).reduce((sum, n) => sum + n, 0),
    [unreadByConversation],
  );

  // Subscription viva app-wide → mesma cache entry do badge da aba Fila (dedup);
  // o ws-listener da fila invalida/refetcha, então o valor fica ao vivo.
  const { data } = useQueueQuery({ filter: 'aguardando' });
  const queue = data?.filters.aguardando ?? 0;

  const both = messages > 0 && queue > 0;
  // face 0 = mensagens, 1 = fila. Só alterna quando os dois têm valor.
  const [face, setFace] = useState<0 | 1>(0);

  useEffect(() => {
    if (!both) {
      setFace(0);
      return;
    }
    const id = setInterval(() => setFace((f) => (f === 0 ? 1 : 0)), ALTERNATE_MS);
    return () => clearInterval(id);
  }, [both]);

  useEffect(() => {
    let title = DEFAULT_TITLE;
    if (messages > 0 && queue > 0) {
      title = face === 0 ? messagesTitle(messages) : queueTitle(queue);
    } else if (messages > 0) {
      title = messagesTitle(messages);
    } else if (queue > 0) {
      title = queueTitle(queue);
    }
    document.title = title;
  }, [messages, queue, face]);

  // Restaura o título ao desmontar (logout / troca de layout).
  useEffect(
    () => () => {
      document.title = DEFAULT_TITLE;
    },
    [],
  );

  return null;
}
